'use client';

/**
 * CommsAction — first-class in-app contact actions that replace the raw
 * tel:/sms:/mailto: deep links scattered across the app.
 *
 * Why this exists: raw `tel:`/`sms:`/`mailto:` links launch the operator's
 * personal phone/mail client with no log, no recording, and no audit trail —
 * replies land on the operator's personal cell and comms get overlooked
 * (comms deep scan 2026-08-04, 12-13 component sites). CommsAction makes
 * every contact action an audited in-app action:
 *
 *  - Text: opens a composer sheet and sends through POST /api/comms/reply,
 *    which routes through sendComm() — opt-out check, TCPA guard, and a
 *    communication_log row. Replies stay inside the compliant pipeline.
 *  - Call: when a GHL contact is linked, opens LeadConnector's contact page
 *    in a new tab so the operator dials from the business number (recorded +
 *    GHL-side logged) — the only GHL-native path to an audited call, since GHL
 *    exposes no REST endpoint to initiate a voice leg. With no linked contact,
 *    falls back to tel: from the operator's personal cell, logging the intent
 *    to communication_log either way so every Call is auditable.
 *  - Email: opens the mailto: client (email replies are out of scope per
 *    CONTEXT.md), displayed as an in-app action, not a raw link.
 *
 * Visual world: inherits the Night Operations Console — near-black, emerald
 * for the send action, ghost for display. Mobile thumb-reach ≥44px.
 */
import { useState, useCallback } from 'react';
import { Phone, MessageSquare, Mail, Loader2 } from 'lucide-react';
import { Button, Sheet, Textarea, InlineError } from '@/components/ui';
import { useToast } from '@/components/ToastProvider';
import { cn } from '@/lib/cn';

type CommsChannel = 'sms' | 'call' | 'email';

export interface CommsActionProps {
  /** GHL contact id — required for the audited Text send. */
  contactId?: string;
  /** Phone (E.164 or display form) for call/sms actions. */
  phone?: string;
  /** Email for the email action. */
  email?: string;
  /** MC customer/property context, attached to the communication_log row. */
  propertyId?: string;
  entityType?: string;
  entityId?: string;
  /** Which actions to surface. Defaults to all available. */
  channels?: CommsChannel[];
  /** Compact variant for inline use in headers/cards. */
  compact?: boolean;
  className?: string;
  /** Fired after the call is logged + tel: launched — lets the host record
   *  a domain-specific side effect (e.g. mark a lead first-touched). */
  onCall?: () => void;
}

const QUICK_REPLIES = [
  'On my way.',
  'Running about 15 min behind, sorry!',
  'Can you confirm your address?',
  'Thanks for the quick reply.',
];

export function CommsAction({
  contactId,
  phone,
  email,
  propertyId,
  entityType,
  entityId,
  channels,
  compact = false,
  className,
  onCall,
}: CommsActionProps) {
  const available: CommsChannel[] = channels ?? [
    ...(phone ? (['sms', 'call'] as CommsChannel[]) : []),
    ...(email ? (['email'] as CommsChannel[]) : []),
  ];

  const [composerOpen, setComposerOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const openComposer = useCallback(() => {
    setError(null);
    setDraft('');
    setComposerOpen(true);
  }, []);

  const sendText = useCallback(async () => {
    if (!contactId) {
      setError('No GHL contact linked for this record — link the contact first.');
      return;
    }
    if (!draft.trim()) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch('/api/comms/reply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactId,
          message: draft.trim(),
          propertyId,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || `Send failed (${res.status})`);
        return;
      }
      setComposerOpen(false);
    } catch {
      setError('Network error — could not reach the comms service.');
    } finally {
      setSending(false);
    }
  }, [contactId, draft, propertyId]);
  const logCall = useCallback(async () => {
    // GHL exposes no REST endpoint to initiate a voice leg — click-to-call
    // lives only inside GHL's web UI. When a GHL contact is linked we open
    // LeadConnector's contact page in a new tab so the operator dials from
    // the business number (recorded + GHL-side logged); the /api/comms/dial
    // route logs the intent to communication_log. With no linked contact, or
    // when GHL isn't configured (409), we fall back to tel: from the
    // operator's personal cell and log the intent via /api/comms/log-call.
    // Either path is auditable; a logging failure never blocks the call.
    if (contactId) {
      // Open the window synchronously, before any await: browsers only honour
      // window.open inside a user gesture, so opening it after the fetch
      // resolves gets silently blocked as a popup.
      const target = window.open('', '_blank');
      try {
        const res = await fetch('/api/comms/dial', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contactId, phone, entityType, entityId, propertyId }),
        });
        if (res.ok) {
          const body = (await res.json().catch(() => null)) as { url?: string } | null;
          if (body?.url) {
            if (target) {
              target.location.href = body.url;
            } else {
              // Popup blocked despite the gesture — fall back to this tab.
              window.location.href = body.url;
            }
            onCall?.();
            toast('Call started — record the outcome in Mission Control when you are done.');
            return;
          }
        }
        const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
        if (body?.error === 'opt_out' || body?.error === 'opt_out_unknown') {
          target?.close();
          // Stop the follow-up entirely — not-interested/DND halts all calls.
          toast(body.message || 'Cannot call: this contact opted out or cannot be reached.');
          return;
        }
        // 409 (crm_not_configured) or missing url → fall through to tel:.
        target?.close();
      } catch {
        target?.close();
      }
    }
    // tel: fallback (no GHL contact, or dialer unavailable). The opt-out
    // paths above return early, so this only runs for the crm_not_configured
    // fallback and for legacy rows that never had a GHL contact_id.
    if (entityId) {
      void fetch('/api/comms/log-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactId, phone, entityType, entityId, propertyId }),
      }).catch(() => {});
    }
    if (phone) {
      window.location.href = `tel:${phone}`;
      onCall?.();
      toast('Call started — record the outcome in Mission Control when you are done.');
    }
  }, [contactId, phone, entityType, entityId, propertyId, onCall, toast]);

  const showText = available.includes('sms') && phone;
  const showCall = available.includes('call') && phone;
  const showEmail = available.includes('email') && email;

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      {showText && (
        <Button
          variant="ghost"
          size={compact ? 'icon' : 'sm'}
          icon={<MessageSquare className={compact ? 'w-4 h-4' : 'w-3.5 h-3.5'} />}
          onClick={openComposer}
          aria-label={`Text ${phone}`}
          title={`Text ${phone}`}
        >
          {!compact && 'Text'}
        </Button>
      )}
      {showCall && (
        <Button
          variant="ghost"
          size={compact ? 'icon' : 'sm'}
          icon={<Phone className={compact ? 'w-4 h-4' : 'w-3.5 h-3.5'} />}
          onClick={logCall}
          aria-label={`Call ${phone}`}
          title={`Call ${phone}`}
        >
          {!compact && 'Call'}
        </Button>
      )}
      {showEmail && (
        <a
          href={`mailto:${email}`}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-zinc-400 hover:bg-bg-elevated hover:text-zinc-200 transition-colors"
          aria-label={`Email ${email}`}
        >
          <Mail className="w-3.5 h-3.5" />
          {!compact && 'Email'}
        </a>
      )}

      <Sheet
        isOpen={composerOpen}
        onClose={() => setComposerOpen(false)}
        title="Send text"
      >
        <div className="flex flex-col gap-3 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <p className="text-xs text-zinc-500">
            To {phone}
            {contactId ? '' : ' — no contact linked'}
          </p>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Type your reply…"
            rows={4}
            maxLength={1600}
            autoFocus
            disabled={sending}
          />
          <div className="flex flex-wrap gap-1.5">
            {QUICK_REPLIES.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => setDraft(q)}
                className="rounded-full border border-white/10 bg-bg-surface px-2.5 py-1 text-[11px] text-zinc-400 hover:border-border-strong hover:text-zinc-200 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
          {error && <InlineError>{error}</InlineError>}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" size="md" onClick={() => setComposerOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="primarySolid"
              size="md"
              onClick={sendText}
              disabled={sending || !draft.trim()}
              icon={sending ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}
            >
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
